import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import {
  areStoredValuesEqual,
  clearCalculatorState,
  createSerializableCalculatorState,
  getValueAtPath,
  hasMeaningfulCalculatorState,
  hasValueAtPath,
  readCalculatorState,
  setValueAtPath,
  writeCalculatorState,
} from "../utils/calculatorStorage";

const SAVE_DEBOUNCE_MS = 400;

const CalculatorPersistenceApiContext = createContext(null);
const CalculatorPersistenceStatusContext = createContext(null);

export function CalculatorPersistenceProvider({ children }) {
  const stateRef = useRef(readCalculatorState() || {});
  const [hasSavedState, setHasSavedState] = useState(() =>
    hasMeaningfulCalculatorState(stateRef.current)
  );
  const dirtyRef = useRef(false);
  const listenersRef = useRef(new Set());
  const saveTimerRef = useRef(null);

  const notifySubscribers = useCallback(() => {
    listenersRef.current.forEach((listener) => listener());
  }, []);

  const subscribe = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const getSnapshot = useCallback(() => stateRef.current, []);

  const flushCalculatorState = useCallback(() => {
    if (!dirtyRef.current) return;

    const serializableState = createSerializableCalculatorState(stateRef.current);

    if (hasMeaningfulCalculatorState(serializableState)) {
      const saved = writeCalculatorState(serializableState);
      if (saved) {
        setHasSavedState(true);
      }
    } else {
      clearCalculatorState();
      setHasSavedState(false);
    }

    dirtyRef.current = false;
  }, []);

  const scheduleSave = useCallback(() => {
    if (typeof window === "undefined") return;

    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = window.setTimeout(() => {
      saveTimerRef.current = null;
      flushCalculatorState();
    }, SAVE_DEBOUNCE_MS);
  }, [flushCalculatorState]);

  const setCalculatorValue = useCallback((path, valueOrUpdater, fallback) => {
    const previousState = stateRef.current;
    const previousValue = hasValueAtPath(previousState, path)
      ? getValueAtPath(previousState, path)
      : fallback;
    const nextValue =
      typeof valueOrUpdater === "function"
        ? valueOrUpdater(previousValue)
        : valueOrUpdater;

    if (areStoredValuesEqual(previousValue, nextValue)) {
      return;
    }

    dirtyRef.current = true;
    stateRef.current = setValueAtPath(previousState, path, nextValue);
    notifySubscribers();
    scheduleSave();
  }, [notifySubscribers, scheduleSave]);

  const clearAllCalculatorState = useCallback(() => {
    if (typeof window !== "undefined" && saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    dirtyRef.current = false;
    stateRef.current = {};
    clearCalculatorState();
    setHasSavedState(false);
    notifySubscribers();
  }, [notifySubscribers]);

  useEffect(() => () => {
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
    }
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushCalculatorState();
      }
    };

    window.addEventListener("pagehide", flushCalculatorState);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", flushCalculatorState);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [flushCalculatorState]);

  const apiValue = useMemo(
    () => ({
      clearAllCalculatorState,
      getSnapshot,
      subscribe,
      setCalculatorValue,
    }),
    [clearAllCalculatorState, getSnapshot, subscribe, setCalculatorValue]
  );

  return (
    <CalculatorPersistenceApiContext.Provider value={apiValue}>
      <CalculatorPersistenceStatusContext.Provider value={hasSavedState}>
        {children}
      </CalculatorPersistenceStatusContext.Provider>
    </CalculatorPersistenceApiContext.Provider>
  );
}

export function useCalculatorPersistence() {
  const apiContext = useContext(CalculatorPersistenceApiContext);

  if (!apiContext) {
    throw new Error("useCalculatorPersistence must be used within CalculatorPersistenceProvider");
  }

  return {
    ...apiContext,
    hasSavedState: useContext(CalculatorPersistenceStatusContext),
  };
}

export function useCalculatorStoredValue(path, fallback) {
  const context = useContext(CalculatorPersistenceApiContext);
  if (!context) {
    throw new Error("useCalculatorStoredValue must be used within CalculatorPersistenceProvider");
  }

  const { getSnapshot, setCalculatorValue, subscribe } = context;
  const fallbackRef = useRef(fallback);
  const getPathSnapshot = useCallback(() => {
    const calculatorState = getSnapshot();
    return hasValueAtPath(calculatorState, path)
      ? getValueAtPath(calculatorState, path)
      : fallbackRef.current;
  }, [getSnapshot, path]);
  const value = useSyncExternalStore(subscribe, getPathSnapshot, getPathSnapshot);

  const setValue = useCallback(
    (nextValue) => {
      setCalculatorValue(path, nextValue, fallbackRef.current);
    },
    [path, setCalculatorValue]
  );

  return [value, setValue];
}
